import type { TenantSlice, TileColumn, TileResult, TileState, WidgetType } from "@csbi/contracts"
import type { MetricDefinition, ValueType } from "@csbi/metric-catalog"
import { formatPeriod } from "./format.js"
import { ordinalColor, singleHueColor, tenantColor, type ThemeMode } from "./palette.js"
import { resolveWidget, type Downgrade } from "./downgrade.js"
import { isSingleHueForm, presentationFor, type SeriesPresentation } from "./series-rules.js"

export type VizSeries = {
  id: string
  name: string
  color: string
  tenantId?: string
  metricId: string
  unit: ValueType
  /** This customer's four-state. `stale` is drawn at reduced opacity; `empty` and `unsupported` never enter the series at all */
  state: TileState
  stateDetail?: string
  approximate: boolean
  data: (number | null)[]
}

export const UNCATEGORISED = "(uncategorised)"

export type VizModel = {
  widget: WidgetType
  downgrade?: Downgrade
  categories: string[]
  rawCategories: string[]
  timeAxis: boolean
  series: VizSeries[]
  omitted: { tenantId: string; displayName: string; state: TileState; detail?: string }[]
  unit: ValueType
  presentation: SeriesPresentation
  singleHue: boolean
  isBlank: boolean
}

export type BuildOptions = {
  mode: ThemeMode
  widget: WidgetType
  definitions: ReadonlyMap<string, MetricDefinition>
  granularity?: "day" | "week" | "month" | "all"
  categoryLabel?: (raw: string) => string
}

const roleIs = (columns: readonly TileColumn[], role: TileColumn["role"]) =>
  columns.filter((c) => c.role === role)

const isOmitted = (state: TileState) => state === "empty" || state === "unsupported"

export function buildVizModel(result: TileResult, opts: BuildOptions): VizModel {
  const { mode, definitions } = opts
  const timeCol = roleIs(result.columns, "time")[0]
  const dimCols = roleIs(result.columns, "dimension")
  const measureCols = roleIs(result.columns, "measure")

  const tenantCol = dimCols.find((c) => c.key.endsWith(".tenant_id"))
  const otherDims = dimCols.filter((c) => c !== tenantCol)

  const axisCol = timeCol ?? otherDims[0]
  const rawCategories = axisCol
    ? [...new Set(result.rows.map((r) => String(r[axisCol.key] ?? "")))]
    : [""]
  const granularity = opts.granularity ?? "day"
  const categories = axisCol
    ? rawCategories.map((v) =>
        timeCol ? formatPeriod(v, granularity) : (opts.categoryLabel?.(v) ?? v) || UNCATEGORISED,
      )
    : ["Total"]

  const drawn = result.perTenant.filter((t) => !isOmitted(t.state))
  const omitted = result.perTenant.filter((t) => isOmitted(t.state)).map((t) => ({
    tenantId: t.tenantId,
    displayName: t.displayName,
    state: t.state,
    ...(t.stateDetail ? { detail: t.stateDetail } : {}),
  }))

  const units = [...new Set(measureCols.map((c) => c.unit ?? "count"))]
  const unit = (units[0] ?? "count") as ValueType

  const singleHue = isSingleHueForm(opts.widget, Boolean(tenantCol) && drawn.length > 1)

  const series: VizSeries[] = []
  const tenantsForSeries: (TenantSlice | undefined)[] = tenantCol
    ? drawn
    : [undefined]

  for (const tenant of tenantsForSeries) {
    for (const [mIdx, col] of measureCols.entries()) {
      const metricId = col.metricId ?? col.key
      const def = definitions.get(metricId)
      const rows = result.rows.filter((r) =>
        tenant && tenantCol ? String(r[tenantCol.key] ?? "") === tenant.tenantId : true,
      )
      const byCategory = new Map(
        rows.map((r) => [axisCol ? String(r[axisCol.key] ?? "") : "", r[col.key]]),
      )

      const data = rawCategories.map((cat) => {
        const raw = byCategory.get(cat)
        // A missing period is null. ECharts breaks the line or leaves a gap rather than drawing down to 0
        return typeof raw === "number" ? raw : raw == null || raw === "" ? null : Number(raw)
      })

      // A series with no points at all is not drawn — otherwise an empty line hangs in the legend and reads as 0
      if (data.every((v) => v == null)) continue

      const color = singleHue
        ? singleHueColor(mIdx, measureCols.length, mode)
        : tenant
          ? tenantColor(tenant.colorSlot, mode)
          : ordinalColor(mIdx, mode)

      series.push({
        id: tenant ? `${tenant.tenantId}::${metricId}` : metricId,
        name: seriesName(tenant, def, metricId, measureCols.length > 1, Boolean(tenantCol)),
        color,
        ...(tenant ? { tenantId: tenant.tenantId } : {}),
        metricId,
        unit: (col.unit ?? "count") as ValueType,
        state: tenant ? tenant.state : result.state,
        ...(tenant?.stateDetail ? { stateDetail: tenant.stateDetail } : {}),
        approximate: def?.approximate === true,
        data,
      })
    }
  }

  const resolved = resolveWidget({
    widget: opts.widget,
    categories: rawCategories.length,
    series: series.length,
    units,
  })

  return {
    widget: resolved.widget,
    ...(resolved.downgrade ? { downgrade: resolved.downgrade } : {}),
    categories,
    rawCategories,
    timeAxis: Boolean(timeCol),
    series,
    omitted,
    unit,
    presentation: presentationFor(series.length),
    singleHue,
    isBlank: series.length === 0,
  }
}

function seriesName(
  tenant: TenantSlice | undefined,
  def: MetricDefinition | undefined,
  metricId: string,
  multiMetric: boolean,
  hasTenantDim: boolean,
): string {
  const metricName = def?.name ?? metricId
  if (!tenant) return metricName
  if (multiMetric && hasTenantDim) return `${tenant.displayName} · ${metricName}`
  return tenant.displayName
}

export function hasStale(model: VizModel): boolean {
  return model.series.some((s) => s.state === "stale")
}

export function denominatorTenants(result: TileResult): TenantSlice[] {
  return result.perTenant.filter((t) => t.state !== "unsupported")
}
